<template>
    <fullscreen-drawer
        v-if="featureFlags['live-site-responsive-design']"
        :visible="visible"
        @close="handleClose"
    >
        <choose-new-payout-method
            v-if="page === CHOOSE_PAYOUT_METHOD"
            :payout-account="payoutAccount"
            @payoutmethod="choosePage" />

        <grin-card-info
            v-if="page === GRIN_CARD_INFO"
            @continue="handleGrinPayInfoContinue"
            @back="handleGrinPayInfoBack"
        />

        <grin-card-signup
            v-if="page === GRIN_CARD_SIGNUP"
            @success="choosePage(GRIN_CARD_CONFIRMATION)"
            @back="handleGrinPaySignupBack"
        />

        <add-debit-card
            v-if="page === DEBIT_CARD_ADD"
            :payout-account="payoutAccount"
            @success="handleAddDebitCardSuccess"
            @back="handleAddDebitCardBack" />

        <add-bank-account
            v-if="page === BANK_ACCOUNT_ADD"
            :payout-account="payoutAccount"
            @success="handleAddBankAccountSuccess"
            @back="handleAddBankAccountBack" />

        <confirmation
            v-if="isConfirmationPage"
            :image="confirmationImage"
            :title="confirmationTitle"
            :subtitle="confirmationSubtitle"
            :button-text="confirmationButtonText"
            @close="handleClose" />
    </fullscreen-drawer>

    <g-drawer
        v-else
        :visible="visible"
        :wrap-style="wrapStyle"
        height="100%"
        get-container="#app"
        placement="bottom"
        @close="handleClose"
    >
        <choose-new-payout-method
            v-if="page === CHOOSE_PAYOUT_METHOD"
            :payout-account="payoutAccount"
            @payoutmethod="choosePage" />

        <grin-card-info
            v-if="page === GRIN_CARD_INFO"
            @continue="handleGrinPayInfoContinue"
            @back="handleGrinPayInfoBack"
        />

        <grin-card-signup
            v-if="page === GRIN_CARD_SIGNUP"
            @success="choosePage(GRIN_CARD_CONFIRMATION)"
            @back="handleGrinPaySignupBack"
        />

        <add-debit-card
            v-if="page === DEBIT_CARD_ADD"
            :payout-account="payoutAccount"
            @success="handleAddDebitCardSuccess"
            @back="handleAddDebitCardBack" />

        <add-bank-account
            v-if="page === BANK_ACCOUNT_ADD"
            :payout-account="payoutAccount"
            @success="handleAddBankAccountSuccess"
            @back="handleAddBankAccountBack" />

        <confirmation
            v-if="isConfirmationPage"
            :image="confirmationImage"
            :title="confirmationTitle"
            :subtitle="confirmationSubtitle"
            :button-text="confirmationButtonText"
            @close="handleClose" />
    </g-drawer>
</template>

<script>
    import { ref, computed } from 'vue-demi';
    import { get } from 'lodash';
    import {
        CHOOSE_PAYOUT_METHOD,
        GRIN_CARD_CONFIRMATION,
        GRIN_CARD_INFO,
        GRIN_CARD_SIGNUP,
        GRIN_CARD_ARRIVAL_TIME,
        PAYOUT_METHOD_TYPE_GRIN_DEBIT_CARD,
        PAYOUT_METHOD_TYPE_CARD,
        PAYOUT_METHOD_TYPE_BANK_MANUAL,
        DEBIT_CARD_ADD,
        DEBIT_CARD_CONFIRMATION,
        PAYOUT_CONFIRMATIONS,
        BANK_ACCOUNT_ADD,
        BANK_ACCOUNT_CONFIRMATION
    } from '../../../../../app-v2/constants/payouts';
    import {
        EVENT_NAME_PAYOUTS_ADD_METHOD_CLOSED,
        EVENT_NAME_PAYOUTS_GRIN_PAY_BENEFITS_VIEWED,
        EVENT_NAME_PAYOUTS_GRIN_PAY_BENEFITS_CLOSED,
        EVENT_NAME_PAYOUTS_GRIN_PAY_BENEFITS_CONTINUED,
        EVENT_NAME_PAYOUTS_GRIN_PAY_BENEFITS_BACK,
        EVENT_NAME_PAYOUTS_GRIN_PAY_ADDRESS_VIEWED,
        EVENT_NAME_PAYOUTS_GRIN_PAY_ADDRESS_CLOSED,
        EVENT_NAME_PAYOUTS_GRIN_PAY_ADDRESS_BACK,
        EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_SENT,
        EVENT_NAME_PAYOUTS_DEBIT_CARD_CLOSED,
        EVENT_NAME_PAYOUTS_DEBIT_CARD_BACK,
        EVENT_NAME_PAYOUTS_DEBIT_CARD_ADDED,
        EVENT_NAME_PAYOUTS_MANUAL_ACH_CLOSED,
        EVENT_NAME_PAYOUTS_MANUAL_ACH_BACK,
        EVENT_NAME_PAYOUTS_MANUAL_ACH_ADDED
    } from '../../../../../app-v2/constants/analytics-events';
    import GrinCardInfo from './GrinCardInfo.vue';
    import GrinCardSignup from './GrinCardSignup.vue';
    import ChooseNewPayoutMethod from './ChooseNewPayoutMethod.vue';
    import AddDebitCard from './AddDebitCard.vue';
    import AddBankAccount from './AddBankAccount.vue';
    import Confirmation from './Confirmation.vue';
    import useEventTracking from '../../../../composables/use-event-tracking';
    import useWhiteLabeling from '../../../../composables/use-white-labeling';
    import useContactStore from '../../../../../stores/contact';
    import FullscreenDrawer from '../../../../../app-v2/components/library/FullscreenDrawer.vue';
    import { useFeatureFlagsStore } from '../../../../../stores';

    export default {
        name: 'AddPayoutMethodDrawer',
        props: {
            visible: {
                type: Boolean,
                default: false
            },
            payoutAccount: {
                type: Object,
                default: null
            }
        },
        components: {
            FullscreenDrawer,
            GrinCardSignup,
            GrinCardInfo,
            ChooseNewPayoutMethod,
            AddDebitCard,
            AddBankAccount,
            Confirmation
        },

        setup(props, context) {
            const page = ref(CHOOSE_PAYOUT_METHOD);
            const events = useEventTracking();
            const contact = useContactStore().data;
            const featureFlags = useFeatureFlagsStore().data;

            const wrapStyle = {
                position: 'absolute'
            };

            //methods
            const choosePage = (pageName) => {
                switch (pageName) {
                    case PAYOUT_METHOD_TYPE_GRIN_DEBIT_CARD:
                    case GRIN_CARD_INFO:
                        page.value = GRIN_CARD_INFO;
                        events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_BENEFITS_VIEWED, getEventPayload());
                        break;
                    case GRIN_CARD_SIGNUP:
                        page.value = GRIN_CARD_SIGNUP;
                        events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_ADDRESS_VIEWED, getEventPayload());
                        break;
                    case GRIN_CARD_CONFIRMATION:
                        page.value = GRIN_CARD_CONFIRMATION;
                        events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_CARD_SENT, getEventPayload());
                        break;
                    case PAYOUT_METHOD_TYPE_CARD:
                    case DEBIT_CARD_ADD:
                        page.value = DEBIT_CARD_ADD;
                        break;
                    case DEBIT_CARD_CONFIRMATION:
                        page.value = DEBIT_CARD_CONFIRMATION;
                        events.trackEvent(EVENT_NAME_PAYOUTS_DEBIT_CARD_ADDED, getEventPayload());
                        break;
                    case PAYOUT_METHOD_TYPE_BANK_MANUAL:
                    case BANK_ACCOUNT_ADD:
                        page.value = BANK_ACCOUNT_ADD;
                        break;
                    case BANK_ACCOUNT_CONFIRMATION:
                        page.value = BANK_ACCOUNT_CONFIRMATION;
                        events.trackEvent(EVENT_NAME_PAYOUTS_MANUAL_ACH_ADDED, getEventPayload());
                        break;
                    case CHOOSE_PAYOUT_METHOD:
                    default:
                        page.value = CHOOSE_PAYOUT_METHOD;
                }
            };

            const handleClose = () => {
                context.emit('close');
                setTimeout(() => {
                    choosePage('');
                }, 1000);

                if ([DEBIT_CARD_CONFIRMATION, BANK_ACCOUNT_CONFIRMATION, GRIN_CARD_CONFIRMATION].includes(page.value)) {
                    context.emit('refresh-payout-account');
                }

                if (page.value === GRIN_CARD_INFO) {
                    events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_BENEFITS_CLOSED, getEventPayload());
                } else if (page.value === GRIN_CARD_SIGNUP) {
                    events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_ADDRESS_CLOSED, getEventPayload());
                } else if (page.value === DEBIT_CARD_ADD) {
                    events.trackEvent(EVENT_NAME_PAYOUTS_DEBIT_CARD_CLOSED, getEventPayload());
                } else if (page.value === BANK_ACCOUNT_ADD) {
                    events.trackEvent(EVENT_NAME_PAYOUTS_MANUAL_ACH_CLOSED, getEventPayload());
                } else if (page.value === CHOOSE_PAYOUT_METHOD) {
                    events.trackEvent(EVENT_NAME_PAYOUTS_ADD_METHOD_CLOSED, getEventPayload());
                }
            };

            const handleAddDebitCardBack = () => {
                events.trackEvent(EVENT_NAME_PAYOUTS_DEBIT_CARD_BACK, getEventPayload());
                choosePage(CHOOSE_PAYOUT_METHOD);
            };

            const handleAddDebitCardSuccess = () => {
                choosePage(DEBIT_CARD_CONFIRMATION);
            };

            const handleAddBankAccountBack = () => {
                events.trackEvent(EVENT_NAME_PAYOUTS_MANUAL_ACH_BACK, getEventPayload());
                choosePage(CHOOSE_PAYOUT_METHOD);
            };

            const handleAddBankAccountSuccess = () => {
                choosePage(BANK_ACCOUNT_CONFIRMATION);
            };

            const handleGrinPayInfoContinue = () => {
                events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_BENEFITS_CONTINUED, getEventPayload());
                choosePage(GRIN_CARD_SIGNUP);
            };

            const handleGrinPayInfoBack = () => {
                events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_BENEFITS_BACK, getEventPayload());
                choosePage(CHOOSE_PAYOUT_METHOD);
            };

            const handleGrinPaySignupBack = () => {
                events.trackEvent(EVENT_NAME_PAYOUTS_GRIN_PAY_ADDRESS_BACK, getEventPayload());
                choosePage(GRIN_CARD_INFO);
            };

            const getEventPayload = () => {
                return {
                    contact_id: contact.id,
                    is_whitelabled: useWhiteLabeling().isWhiteLabeled(),
                    has_pending_payout: contact.has_pending_payout,
                    has_additional_payout_method: props.payoutAccount?.external_account?.payout_methods.length > 0,
                    referrer: get(window.SERVER_DATA, 'referer', null)
                };
            };

            const confirmationImage = computed(() => {
                return PAYOUT_CONFIRMATIONS[page.value]?.image;
            });

            const confirmationTitle = computed(() => {
                return PAYOUT_CONFIRMATIONS[page.value]?.title;
            });

            const confirmationSubtitle = computed(() => {
                return PAYOUT_CONFIRMATIONS[page.value]?.subtitle;
            });

            const confirmationButtonText = computed(() => {
                return PAYOUT_CONFIRMATIONS[page.value]?.buttonText;
            });

            const isConfirmationPage = computed(() => {
                return [GRIN_CARD_CONFIRMATION, DEBIT_CARD_CONFIRMATION, BANK_ACCOUNT_CONFIRMATION].includes(
                    page.value
                );
            });

            return {
                //constants
                CHOOSE_PAYOUT_METHOD,
                GRIN_CARD_CONFIRMATION,
                GRIN_CARD_INFO,
                GRIN_CARD_SIGNUP,
                GRIN_CARD_ARRIVAL_TIME,
                DEBIT_CARD_ADD,
                BANK_ACCOUNT_ADD,
                wrapStyle,
                confirmationImage,
                confirmationTitle,
                confirmationSubtitle,
                confirmationButtonText,
                isConfirmationPage,
                featureFlags,

                //state
                page,

                //methods
                choosePage,
                handleClose,
                handleAddDebitCardBack,
                handleAddDebitCardSuccess,
                handleAddBankAccountBack,
                handleAddBankAccountSuccess,
                handleGrinPayInfoContinue,
                handleGrinPayInfoBack,
                handleGrinPaySignupBack
            };
        }
    };
</script>
